/**
 * Analytics consent + visitor identity for the StorySplat viewer runtime.
 *
 * The logic here is a deliberate PORT of the editor app's
 * `src/utils/analyticsConsent.ts` (and its `discover/utils/analyticsConsent.ts`
 * twin), NOT an import: the viewer ships as a standalone npm/CDN package and
 * must not reach into the editor app's source tree. Keep the three in sync by
 * hand — the shared contract is the opt-out signals and the localStorage key,
 * which are user-visible.
 *
 * Before this module existed the only kill switch on the npm/CDN path was the
 * embedder's `disableAnalytics` build flag, so DNT / Global Privacy Control /
 * the opt-out key / `?analytics=off` were all silently ignored by the runtime.
 */

export const ANALYTICS_OPT_OUT_KEY = 'storysplat_analytics_opt_out';
export const VISITOR_ID_KEY = 'storysplat_visitor_id';

type DoNotTrackWindow = Window & { doNotTrack?: string };
type DoNotTrackNavigator = Navigator & {
  msDoNotTrack?: string;
  globalPrivacyControl?: boolean;
};

/**
 * True when the runtime is allowed to collect analytics for this visitor.
 *
 * Honors, in order: the caller's own switch, Do Not Track (standard, IE/Edge
 * legacy `msDoNotTrack`, and the old `window.doNotTrack` placement), Global
 * Privacy Control, `?analytics=off` on the current URL, and the
 * `storysplat_analytics_opt_out` localStorage key.
 */
export const isAnalyticsAllowed = (userAnalyticsEnabled = true): boolean => {
  if (!userAnalyticsEnabled) {
    return false;
  }

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const nav = navigator as DoNotTrackNavigator;
  if (
    nav.doNotTrack === '1' ||
    nav.msDoNotTrack === '1' ||
    (window as DoNotTrackWindow).doNotTrack === '1'
  ) {
    return false;
  }

  if (nav.globalPrivacyControl === true) {
    return false;
  }

  try {
    if (new URL(window.location.href).searchParams.get('analytics') === 'off') {
      return false;
    }
  } catch {
    // Ignore malformed URLs and continue with the remaining checks.
  }

  try {
    const optOut = window.localStorage.getItem(ANALYTICS_OPT_OUT_KEY);
    if (optOut === 'true' || optOut === '1') {
      return false;
    }
  } catch {
    // Blocked storage should not break the viewer or the analytics wrapper.
  }

  return true;
};

/**
 * A UUID that works outside a secure context.
 *
 * `crypto.randomUUID` is secure-context-only: on a plain `http://` embed (a
 * very common self-hosted / WordPress case) it is `undefined`, and calling it
 * threw a TypeError straight out of the SessionRecorder constructor, out of
 * `createViewer`, and the scene never rendered. Degrade instead:
 * `crypto.randomUUID` → `crypto.getRandomValues` → `Math.random`. All three
 * produce a v4-shaped string, which satisfies the API's ID pattern
 * (`/^[a-zA-Z0-9_-]{1,128}$/`).
 */
export function safeRandomUUID(): string {
  const webCrypto: Crypto | undefined =
    typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;

  try {
    if (webCrypto && typeof webCrypto.randomUUID === 'function') {
      return webCrypto.randomUUID();
    }
  } catch {
    // Some engines expose randomUUID but throw outside a secure context.
  }

  try {
    if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
      const bytes = webCrypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
      let hex = '';
      for (let i = 0; i < 16; i++) hex += bytes[i].toString(16).padStart(2, '0');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    // getRandomValues can throw on quota in exotic engines — fall through.
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * The visitor id shared by the SessionRecorder, the view ping and the injected
 * tracker in the HTML endpoint (all use `VISITOR_ID_KEY`).
 *
 * Returns `null` when consent is absent — and crucially writes nothing to
 * localStorage in that case, so an opted-out visitor is never given a
 * persistent identifier.
 *
 * Read order is localStorage → sessionStorage (migration from older viewers)
 * → freshly generated.
 */
export function getOrCreateVisitorId(userAnalyticsEnabled = true): string | null {
  if (!isAnalyticsAllowed(userAnalyticsEnabled)) return null;

  try {
    const existing =
      localStorage.getItem(VISITOR_ID_KEY) || sessionStorage.getItem(VISITOR_ID_KEY) || '';
    const visitorId = existing || safeRandomUUID();
    try {
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    } catch {
      // Storage may be blocked (Safari private mode, third-party iframe).
    }
    return visitorId;
  } catch {
    // Storage access itself threw — still return a usable ephemeral id.
    return safeRandomUUID();
  }
}
